import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Send, Bot, User, Sparkles, Clock, CheckCircle, AlertCircle,
  MessageSquare, History, Settings, Trash2, Archive, ThumbsUp, ThumbsDown,
  Cpu, Zap, ChevronDown, ChevronUp, ExternalLink, Play, FileText,
  Shield, Search, RefreshCw, Loader2, Brain, Server, Crosshair, Target, Eye,
  Rocket, CheckCheck
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { aiAssistantAPI } from '../../services/api/client';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import PageInspector from './PageInspector';
import { usePageDataCache } from '../../services/PageDataCache';
import MarkdownRenderer from '../common/MarkdownRenderer';
import { useI18n } from '../../i18n';

const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

// Enhanced markdown renderer for AI messages with executable action buttons and autonomous actions
const renderMarkdown = (content, onExecuteAction, onExecuteAutonomousAction) => {
  if (!content) return null;

  // Robust parser for [ACTION:{...}] — handles any depth of JSON nesting/arrays
  const extractActionBlocks = (text) => {
    const actions = [];
    const fullMatches = [];
    let i = 0;
    while (i < text.length) {
      const idx = text.indexOf('[ACTION:', i);
      if (idx === -1) break;
      const jsonStart = idx + 8;
      let depth = 0, j = jsonStart, inString = false, escaped = false;
      while (j < text.length) {
        const c = text[j];
        if (escaped) { escaped = false; j++; continue; }
        if (c === '\\') { escaped = true; j++; continue; }
        if (c === '"') { inString = !inString; j++; continue; }
        if (inString) { j++; continue; }
        if (c === '{') depth++;
        else if (c === '}') {
          depth--;
          if (depth === 0) {
            if (j + 1 < text.length && text[j + 1] === ']') {
              const jsonStr = text.slice(jsonStart, j + 1);
              const fullMatch = text.slice(idx, j + 2);
              try {
                actions.push({ data: JSON.parse(jsonStr), fullMatch });
                fullMatches.push(fullMatch);
              } catch (e) {
                console.error('Failed to parse ACTION JSON:', jsonStr);
              }
            }
            break;
          }
        }
        j++;
      }
      i = j + 2;
    }
    return { actions, fullMatches };
  };

  const { actions: autonomousActions, fullMatches: actionFullMatches } = extractActionBlocks(content);

  // Then detect [EXEC:action:params] executable action tags
  const execRegex = /\[EXEC:([^:]+):(\{[^}]+\})\]\s*([^\[]*)/g;
  const execButtons = [];
  let match;

  while ((match = execRegex.exec(content)) !== null) {
    execButtons.push({
      actionKey: match[1],
      params: match[2],
      buttonText: match[3].trim(),
      fullMatch: match[0]
    });
  }

  // Remove all tags from content for normal rendering
  let cleanContent = content;
  actionFullMatches.forEach(fm => { cleanContent = cleanContent.replace(fm, ''); });
  cleanContent = cleanContent.replace(execRegex, '');

  // Use MarkdownRenderer for the main content
  const renderedContent = <MarkdownRenderer content={cleanContent} variant="chat" />;

  const hasButtons = (execButtons.length > 0 && onExecuteAction) || (autonomousActions.length > 0 && onExecuteAutonomousAction);

  if (hasButtons) {
    // Action type labels for display
    const actionLabels = {
      'launch_scan': 'Lancer le scan',
      'generate_report': 'Générer le rapport',
      'list_scans': 'Voir les scans',
      'list_vulnerabilities': 'Voir les vulnérabilités',
      'get_scan_status': 'Statut du scan',
      'search_credentials': 'Rechercher les credentials',
      'mark_false_positive': 'Marquer faux positif',
      'create_workspace': 'Créer le workspace',
      'schedule_scan': 'Programmer le scan',
      'launch_pentest_ad': 'Lancer le pentest AD'
    };

    return (
      <>
        {renderedContent}
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-purple-500/20">
          {/* Autonomous action buttons */}
          {autonomousActions.map((action, idx) => (
            <button
              key={`auto-${idx}`}
              onClick={() => onExecuteAutonomousAction(action.data)}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-sm rounded-lg shadow-lg hover:shadow-green-500/25 transition-all flex items-center gap-2 font-medium"
            >
              <Rocket className="w-4 h-4" />
              {actionLabels[action.data.type] || `Exécuter: ${action.data.type}`}
              {action.data.target ? ` (${action.data.target})` :
               action.data.target_domain ? ` (${action.data.target_domain})` :
               action.data.scan_target ? ` (${action.data.scan_target})` : ''}
            </button>
          ))}
          {/* Legacy EXEC buttons */}
          {execButtons.map((btn, idx) => {
            let params = {};
            try {
              params = JSON.parse(btn.params);
            } catch (e) {
              console.error('Failed to parse action params:', btn.params);
            }

            return (
              <button
                key={`exec-${idx}`}
                onClick={() => onExecuteAction(btn.actionKey, params)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white text-sm rounded-lg shadow-lg hover:shadow-purple-500/25 transition-all flex items-center gap-2 font-medium"
              >
                <Play className="w-4 h-4" />
                {btn.buttonText || `Exécuter ${btn.actionKey}`}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  return renderedContent;
};

const AIAssistantSlider = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { cacheCurrentPage, getCacheSummary, extractPageData } = usePageDataCache();
  const { activeWorkspace } = useWorkspace();

  // Ref to track if initial data has been loaded (component is only mounted when open)
  const hasInitialLoadRef = useRef(false);

  // State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const [gpuStatus, setGpuStatus] = useState({ available: false });
  const [assistantStatus, setAssistantStatus] = useState(null);
  const [error, setError] = useState(null);

  // Inspector mode state
  const [inspectorActive, setInspectorActive] = useState(false);
  const [selectedElements, setSelectedElements] = useState([]);

  // Token tracking
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);

  // Get current page context
  const getPageContext = useCallback(() => {
    return location.pathname;
  }, [location.pathname]);

  // Get page data for context
  const getPageData = useCallback(() => {
    // Extract relevant data from the current page
    const pageData = {
      path: location.pathname,
      search: location.search,
      timestamp: new Date().toISOString()
    };

    // Add URL parameters if any
    const params = new URLSearchParams(location.search);
    params.forEach((value, key) => {
      pageData[key] = value;
    });

    // Include selected elements data from inspector
    if (selectedElements.length > 0) {
      pageData.inspectedElements = selectedElements;
    }

    // Include extracted page data
    const extractedData = extractPageData(location.pathname);
    if (extractedData) {
      pageData.extractedPageData = extractedData;
    }

    // Include cache summary for context
    const cacheSummary = getCacheSummary();
    if (cacheSummary.totalPages > 0) {
      pageData.cacheSummary = cacheSummary;
    }

    return pageData;
  }, [location, selectedElements, extractPageData, getCacheSummary]);

  // Handle inspector element selection
  const handleInspectorSelect = useCallback((elements) => {
    setSelectedElements(elements);
    setInspectorActive(false);
  }, []);

  // Close inspector
  const handleInspectorClose = useCallback(() => {
    setInspectorActive(false);
  }, []);

  // Remove selected element
  const removeSelectedElement = (index) => {
    setSelectedElements(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all selected elements
  const clearSelectedElements = () => {
    setSelectedElements([]);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when opened - delay to wait for animation
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Multiple attempts to ensure focus works after animation
      const timers = [
        setTimeout(() => inputRef.current?.focus(), 100),
        setTimeout(() => inputRef.current?.focus(), 300),
        setTimeout(() => inputRef.current?.focus(), 500)
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [isOpen]);

  // Keep focus on input after message is sent (when loading finishes)
  useEffect(() => {
    if (!isLoading && isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isLoading, isOpen]);

  // Load assistant status - memoized to prevent infinite loops
  const loadAssistantStatus = useCallback(async () => {
    try {
      const response = await aiAssistantAPI.getStatus();
      setAssistantStatus(response);
      setGpuStatus(response.gpu_pool || { available: false });
    } catch (err) {
      console.error('Error loading assistant status:', err);
    }
  }, []);

  // Load conversations - memoized
  const loadConversations = useCallback(async () => {
    try {
      const response = await aiAssistantAPI.listConversations({ page: 1, page_size: 10 });
      setConversations(response.conversations || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, []);

  // Load suggested actions - memoized
  const loadActions = useCallback(async () => {
    try {
      const response = await aiAssistantAPI.getActions(getPageContext());
      setSuggestedActions(response || []);
    } catch (err) {
      console.error('Error loading actions:', err);
    }
  }, [getPageContext]);

  // Load initial data when component mounts (only happens when panel opens)
  useEffect(() => {
    // Only load once when component first mounts
    if (hasInitialLoadRef.current) {
      return;
    }
    hasInitialLoadRef.current = true;

    // Load all data
    loadAssistantStatus();
    loadConversations();
    loadActions();
    cacheCurrentPage();
    // Empty dependency array - only run on mount
  }, []);

  // Reload actions when page changes
  useEffect(() => {
    if (isOpen) {
      loadActions();
    }
  }, [location.pathname, isOpen, loadActions]);

  // Send message
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const currentSelectedElements = [...selectedElements];
    setInput('');
    setError(null);

    // Build context message with inspected elements
    let contextText = '';
    if (currentSelectedElements.length > 0) {
      contextText = `\n\n[Éléments inspectés: ${currentSelectedElements.length}]`;
      currentSelectedElements.forEach((el) => {
        contextText += `\n• ${el.path}`;
        if (el.tableData) contextText += ` (Table: ${el.tableData.totalRows} lignes)`;
        if (el.vulnerabilityContext?.cve) contextText += ` (${el.vulnerabilityContext.cve})`;
      });
    }

    // Add user message to UI
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage + contextText,
      inspectedElements: currentSelectedElements.length > 0 ? currentSelectedElements : null,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setSelectedElements([]);

    // Add placeholder streaming message
    const streamingId = `streaming-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: streamingId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      created_at: new Date().toISOString()
    }]);

    try {
      const token = localStorage.getItem('access_token');
      const wsId = activeWorkspace?.id;
      const requestBody = {
        message: userMessage,
        conversation_id: conversationId,
        page_context: getPageContext(),
        page_data: {
          ...getPageData(),
          workspace_id: wsId || undefined,
          inspectedElements: currentSelectedElements.length > 0 ? currentSelectedElements : undefined
        },
        language: 'fr'
      };

      // In demo mode, short-circuit the SSE streaming endpoint in favour
      // of the canned /chat-demo endpoint (plain JSON). We simulate the
      // progressive token stream client-side so the UX stays identical.
      if (IS_DEMO_MODE) {
        const demoResp = await fetch('/api/v1/ai/chat-demo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ question: userMessage })
        });
        if (!demoResp.ok) {
          const errData = await demoResp.json().catch(() => ({}));
          throw new Error(errData.detail?.message || errData.detail || `HTTP ${demoResp.status}`);
        }
        const demoData = await demoResp.json();
        const full = demoData.answer || '';
        const speed = demoData.typewriter_speed_ms || 30;
        // Progressive reveal — char by char — to mimic streaming.
        await new Promise((resolve) => {
          let i = 0;
          const iv = setInterval(() => {
            i++;
            const slice = full.slice(0, i);
            setMessages(prev => prev.map(m =>
              m.id === streamingId ? { ...m, content: slice } : m
            ));
            if (i >= full.length) {
              clearInterval(iv);
              setMessages(prev => prev.map(m =>
                m.id === streamingId
                  ? { ...m, content: full, isStreaming: false, ai_model_used: 'demo' }
                  : m
              ));
              resolve();
            }
          }, speed);
        });
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/v1/ai-assistant/chat-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(wsId ? { 'X-Workspace-ID': wsId } : {})
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep last incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.token !== undefined) {
              fullContent += data.token;
              setMessages(prev => prev.map(m =>
                m.id === streamingId ? { ...m, content: fullContent } : m
              ));
            } else if (data.done) {
              if (data.is_new_conversation && data.conversation_id) {
                setConversationId(data.conversation_id);
              }
              setMessages(prev => prev.map(m =>
                m.id === streamingId
                  ? { ...m, id: data.message_id || streamingId, content: fullContent, isStreaming: false, ai_model_used: 'qwen2.5-coder:32b' }
                  : m
              ));
            } else if (data.error) {
              throw new Error(data.error);
            }
          } catch (parseErr) {
            // Ignore malformed SSE lines
          }
        }
      }

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Une erreur est survenue');
      setMessages(prev => prev.map(m =>
        m.id === streamingId
          ? { ...m, content: `Erreur: ${err.message || 'Je suis désolé, une erreur est survenue. Veuillez réessayer.'}`, isStreaming: false, isError: true }
          : m
      ));
    } finally {
      setIsLoading(false);
    }
  };

  // Load conversation
  const loadConversation = async (convId) => {
    try {
      setIsLoading(true);
      const response = await aiAssistantAPI.getConversation(convId);
      setConversationId(convId);
      setMessages(response.messages || []);
      setShowHistory(false);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Erreur lors du chargement de la conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
    setError(null);
    setTotalTokensUsed(0);
  };

  // Delete conversation
  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await aiAssistantAPI.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (conversationId === convId) {
        startNewConversation();
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  // Execute action - now starts a guided conversation
  const executeAction = async (action) => {
    try {
      // Check action type - some actions are direct, others are guided
      if (action.action_type === 'navigate') {
        // Direct navigation - use config or call API
        const navigateTo = action.action_config?.navigate_to || action.url;
        if (navigateTo) {
          window.location.href = navigateTo;
          return;
        }
        // Fallback to API
        const result = await aiAssistantAPI.executeAction({
          action_key: action.action_key,
          page_context: getPageContext()
        });
        if (result.success && result.result?.navigate_to) {
          window.location.href = result.result.navigate_to;
        }
        return;
      }

      if (action.action_type === 'modal' || action.action_type === 'open_modal') {
        // Trigger modal - dispatch custom event
        const modalType = action.action_config?.modal || action.modal_id;
        if (modalType) {
          window.dispatchEvent(new CustomEvent('openModal', { detail: { type: modalType } }));
        }
        return;
      }

      if (action.action_type === 'external') {
        // Direct external link (including mailto:)
        const externalUrl = action.action_config?.external_url || action.url;
        if (externalUrl) {
          if (externalUrl.startsWith('mailto:')) {
            window.location.href = externalUrl;
          } else {
            window.open(externalUrl, '_blank');
          }
          return;
        }
        const result = await aiAssistantAPI.executeAction({
          action_key: action.action_key,
          page_context: getPageContext()
        });
        if (result.success && result.result?.external_url) {
          window.open(result.result.external_url, '_blank');
        }
        return;
      }

      // Platform interaction actions
      if (action.action_type === 'fill_input') {
        // Fill a form field
        const selector = action.selector || action.target_selector;
        const value = action.value || action.parameters?.value;
        if (selector && value !== undefined) {
          const element = document.querySelector(selector);
          if (element) {
            element.value = value;
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
            // Highlight briefly
            element.style.boxShadow = '0 0 10px 3px rgba(147, 51, 234, 0.5)';
            setTimeout(() => element.style.boxShadow = '', 1500);
          }
        }
        return;
      }

      if (action.action_type === 'click_button') {
        // Click a button
        const selector = action.selector || action.target_selector;
        if (selector) {
          const element = document.querySelector(selector);
          if (element) {
            element.click();
          }
        }
        return;
      }

      if (action.action_type === 'select_option') {
        // Select from dropdown
        const selector = action.selector || action.target_selector;
        const value = action.value || action.parameters?.value;
        if (selector && value !== undefined) {
          const element = document.querySelector(selector);
          if (element) {
            element.value = value;
            element.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        return;
      }

      if (action.action_type === 'scroll_to') {
        // Scroll to an element
        const selector = action.selector || action.target_selector;
        if (selector) {
          const element = document.querySelector(selector);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight briefly
            element.style.boxShadow = '0 0 10px 3px rgba(147, 51, 234, 0.5)';
            setTimeout(() => element.style.boxShadow = '', 2000);
          }
        }
        return;
      }

      if (action.action_type === 'toggle') {
        // Toggle a checkbox/switch
        const selector = action.selector || action.target_selector;
        if (selector) {
          const element = document.querySelector(selector);
          if (element) {
            element.checked = !element.checked;
            element.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        return;
      }

      // For all other actions (api_call, modal, guided), start a guided conversation
      // Create a prompt that asks the AI to guide the user through this action
      const actionPrompt = `[ACTION:${action.action_key}] Je veux ${action.name_fr || action.name}. Guide-moi étape par étape.`;

      // Add user message
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'user',
        content: actionPrompt,
        created_at: new Date().toISOString(),
        isActionRequest: true,
        actionData: action
      }]);

      setIsLoading(true);
      setError(null);

      // Send to AI for guided response
      const response = await aiAssistantAPI.sendMessage({
        message: actionPrompt,
        conversation_id: conversationId,
        page_context: getPageContext(),
        page_data: {
          ...getPageData(),
          actionRequest: {
            action_key: action.action_key,
            action_type: action.action_type,
            action_config: action.action_config,
            name: action.name,
            name_fr: action.name_fr,
            description: action.description,
            description_fr: action.description_fr
          }
        },
        language: 'fr'
      });

      if (!conversationId && response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      // Add AI response with potential action buttons
      setMessages(prev => [...prev, {
        id: response.message.id,
        message_id: response.message.message_id,
        role: 'assistant',
        content: response.message.content,
        ai_model_used: response.message.ai_model_used,
        ai_tokens_input: response.message.ai_tokens_input,
        ai_tokens_output: response.message.ai_tokens_output,
        ai_response_time_ms: response.message.ai_response_time_ms,
        suggested_actions: response.message.suggested_actions,
        created_at: response.message.created_at
      }]);

      // Update suggested actions
      if (response.suggested_actions?.length > 0) {
        setSuggestedActions(response.suggested_actions);
      }

      // Track tokens
      if (response.message.ai_tokens_input || response.message.ai_tokens_output) {
        setTotalTokensUsed(prev => prev +
          (response.message.ai_tokens_input || 0) +
          (response.message.ai_tokens_output || 0)
        );
      }

    } catch (err) {
      console.error('Error executing action:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: `Erreur: ${err.message || 'Impossible d\'exécuter l\'action'}`,
        isError: true,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Submit feedback
  const submitFeedback = async (messageId, helpful) => {
    try {
      await aiAssistantAPI.submitFeedback(messageId, { helpful });
      // Update message in UI
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, helpful } : m
      ));
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  // Execute an API action from AI response buttons
  const handleExecuteAPIAction = useCallback(async (actionKey, params) => {
    setIsLoading(true);

    // Add execution message
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      content: `▶️ Exécution: ${actionKey}`,
      created_at: new Date().toISOString(),
      isSystemMessage: true
    }]);

    try {
      // Call the API to execute the action
      const result = await aiAssistantAPI.executeAction({
        action_key: actionKey,
        parameters: params,
        page_context: getPageContext()
      });

      if (result.success) {
        // Build a success message with link if available
        let successContent = `✅ **${result.message || 'Action exécutée avec succès'}**`;

        // Add scan details if it's a scan
        if (actionKey === 'start_scan' && result.result) {
          successContent += `\n\n📊 **Détails du scan:**`;
          successContent += `\n- Cible: \`${result.result.target || params.target}\``;
          successContent += `\n- Type: ${result.result.scan_type || params.scan_type}`;
          successContent += `\n- Statut: ${result.result.status || 'En attente'}`;

          if (result.result.navigate_to) {
            // Store the link for the button
            successContent += `\n\n🔗 **Accéder au scan:**`;
          }
        }

        // Add the success message with navigation link
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: successContent,
          created_at: new Date().toISOString(),
          actionLink: result.result?.navigate_to ? {
            url: result.result.navigate_to,
            label: actionKey === 'start_scan' ? '📊 Voir le scan en cours' :
                   actionKey === 'generate_report' ? '📄 Voir le rapport' :
                   '🔗 Accéder'
          } : null
        }]);

        // Don't auto-navigate - let user click the link
        // if (result.result?.navigate_to) {
        //   window.location.href = result.result.navigate_to;
        // }
      } else {
        // Report error to AI
        const errorMessage = `L'action "${actionKey}" a échoué: ${result.error || 'Erreur inconnue'}`;

        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: `❌ ${errorMessage}`,
          isError: true,
          created_at: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Error executing API action:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: `❌ Erreur lors de l'exécution: ${err.message || 'Erreur inconnue'}`,
        isError: true,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, getPageContext, getPageData]);

  // Execute autonomous action from [ACTION:{...}] tag
  const handleExecuteAutonomousAction = useCallback(async (actionData) => {
    setIsLoading(true);

    const actionLabels = {
      'launch_scan': 'Lancement du scan',
      'generate_report': 'Génération du rapport',
      'list_scans': 'Récupération des scans',
      'list_vulnerabilities': 'Récupération des vulnérabilités',
      'get_scan_status': 'Vérification du statut',
      'search_credentials': 'Recherche de credentials',
      'create_workspace': 'Création du workspace',
    };

    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      content: `🚀 ${actionLabels[actionData.type] || actionData.type}${actionData.target ? ` sur ${actionData.target}` : actionData.target_domain ? ` sur ${actionData.target_domain}` : actionData.scan_target ? ` pour ${actionData.scan_target}` : ''}...`,
      created_at: new Date().toISOString(),
      isSystemMessage: true
    }]);

    try {
      const wsId = activeWorkspace?.id;
      // Inject workspace_id into action if not already set
      const enrichedAction = wsId ? { workspace_id: wsId, ...actionData } : actionData;
      const response = await fetch('/api/v1/ai-assistant/actions/execute-autonomous', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          ...(wsId ? { 'X-Workspace-ID': wsId } : {})
        },
        body: JSON.stringify(enrichedAction)
      });
      const result = await response.json();

      if (result.success) {
        let successContent = `✅ **${result.message || 'Action exécutée avec succès'}**`;

        if (result.scans) {
          successContent += '\n\n| Cible | Type | Statut | Vulns |\n|---|---|---|---|\n';
          result.scans.forEach(s => {
            successContent += `| ${s.target} | ${s.scan_type} | ${s.status} | ${s.total_vulnerabilities} |\n`;
          });
        }

        if (result.vulnerabilities) {
          successContent += '\n\n| CVE | Titre | Sévérité | CVSS |\n|---|---|---|---|\n';
          result.vulnerabilities.forEach(v => {
            successContent += `| ${v.cve_id} | ${v.title?.substring(0, 40)} | ${v.severity} | ${v.cvss_score} |\n`;
          });
        }

        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: successContent,
          created_at: new Date().toISOString(),
          actionLink: result.navigate_to ? {
            url: result.navigate_to,
            label: actionData.type === 'launch_scan' ? '📊 Voir le scan' :
                   actionData.type === 'generate_report' ? '📄 Voir le rapport' :
                   '🔗 Accéder'
          } : null
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: `❌ ${result.error || 'Erreur lors de l\'exécution'}`,
          isError: true,
          created_at: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Autonomous action error:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: `❌ Erreur: ${err.message || 'Impossible d\'exécuter l\'action'}`,
        isError: true,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Poll workflow status periodically
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(async () => {
      try {
        const response = await fetch('/api/v1/ai-assistant/workflows/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        const data = await response.json();
        if (data.completed && data.completed.length > 0) {
          data.completed.forEach(w => {
            setMessages(prev => {
              const alreadyNotified = prev.some(m => m.workflowId === w.workflow_id);
              if (alreadyNotified) return prev;
              return [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: `✅ **Workflow terminé**: ${w.type === 'scan_then_report' ? 'Scan terminé, rapport généré automatiquement' : 'Tâche terminée'}`,
                created_at: new Date().toISOString(),
                workflowId: w.workflow_id
              }];
            });
          });
        }
      } catch (e) {
        // Silent fail for polling
      }
    }, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay - Hidden during inspector mode */}
      {!inspectorActive && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Slider Panel - Hidden during inspector mode */}
      <div className={`fixed top-0 right-0 h-full w-full max-w-md bg-gradient-to-b from-purple-950/95 to-brand-black-light border-l border-purple-500/30 z-50 overflow-hidden flex flex-col animate-slide-in-right shadow-2xl shadow-purple-500/20 transition-all duration-300 ${
        inspectorActive ? 'opacity-0 pointer-events-none translate-x-full' : 'opacity-100'
      }`}>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-violet-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Assistant IA</h2>
              <p className="text-purple-200 text-xs">{t('footer.poweredBy')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInspectorActive(true)}
              className={`relative p-2 rounded-lg transition-colors ${
                selectedElements.length > 0
                  ? 'bg-green-500/30 hover:bg-green-500/40 text-green-300'
                  : 'hover:bg-white/20 text-white'
              }`}
              title="Mode Inspecteur"
            >
              <Crosshair className="w-5 h-5" />
              {selectedElements.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                  {selectedElements.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Historique"
            >
              <History className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={startNewConversation}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Nouvelle conversation"
            >
              <MessageSquare className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Compute Status Bar */}
        <div className="px-4 py-2 bg-purple-900/50 border-b border-purple-500/20">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <Cpu className={`w-4 h-4 ${gpuStatus.available ? 'text-green-400' : 'text-red-400'}`} />
              <span className={gpuStatus.available ? 'text-green-400' : 'text-red-400'}>
                {gpuStatus.compute_type || (gpuStatus.has_gpu ? 'GPU' : 'CPU')} {gpuStatus.available ? 'Disponible' : 'Indisponible'}
              </span>
              {gpuStatus.available && gpuStatus.queue_size === 1 && (
                <span className="text-blue-400 text-xs">
                  (1 requête en cours)
                </span>
              )}
              {gpuStatus.available && gpuStatus.queue_size > 1 && (
                <span className="text-yellow-400 text-xs">
                  ({gpuStatus.queue_size - 1} en attente)
                </span>
              )}
            </div>
            {assistantStatus && (
              <div className="flex items-center gap-3 text-purple-300">
                <span>{assistantStatus.active_conversations || 0} conv.</span>
                <span>{assistantStatus.total_messages_today || 0} msg</span>
              </div>
            )}
          </div>
          {/* Memory & Model Info */}
          {gpuStatus.available && (
            <div className="flex items-center justify-between text-xs mt-1.5 pt-1.5 border-t border-purple-500/10">
              <div className="flex items-center gap-3 text-purple-400">
                {gpuStatus.has_gpu && gpuStatus.gpu_memory_used != null && gpuStatus.gpu_memory_total != null && (
                  <span>VRAM: {gpuStatus.gpu_memory_used.toFixed(1)}/{gpuStatus.gpu_memory_total.toFixed(0)} GB</span>
                )}
                {!gpuStatus.has_gpu && gpuStatus.ram_used_gb != null && gpuStatus.ram_total_gb != null && (
                  <span>RAM: {gpuStatus.ram_used_gb.toFixed(1)}/{gpuStatus.ram_total_gb.toFixed(0)} GB</span>
                )}
                {gpuStatus.models_loaded?.length > 0 && (
                  <span className="text-purple-300">{gpuStatus.models_loaded.length} modèle{gpuStatus.models_loaded.length > 1 ? 's' : ''}</span>
                )}
              </div>
              {totalTokensUsed > 0 && (
                <div className="flex items-center gap-1 text-purple-300">
                  <Zap className="w-3 h-3" />
                  <span>{totalTokensUsed.toLocaleString()} tokens</span>
                </div>
              )}
              {assistantStatus?.avg_response_time_ms && (
                <span className="text-purple-400">
                  ~{Math.round(assistantStatus.avg_response_time_ms)}ms/req
                </span>
              )}
            </div>
          )}
          {/* Chat ID for troubleshooting */}
          {conversationId && (
            <div className="flex items-center justify-between text-xs mt-1.5 pt-1.5 border-t border-purple-500/10">
              <div className="flex items-center gap-2 text-purple-400">
                <MessageSquare className="w-3 h-3" />
                <span>ID Chat:</span>
                <code
                  className="bg-purple-900/50 px-1.5 py-0.5 rounded text-purple-300 cursor-pointer hover:bg-purple-800/50 transition-colors"
                  onClick={() => {
                    navigator.clipboard.writeText(conversationId);
                    // Small visual feedback
                    const el = document.activeElement;
                    if (el) el.blur();
                  }}
                  title="Cliquer pour copier"
                >
                  {conversationId.substring(0, 8)}...
                </code>
              </div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">

          {/* History Panel */}
          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              <h3 className="text-purple-300 font-medium mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Conversations récentes
              </h3>
              {conversations.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">
                  Aucune conversation
                </p>
              ) : (
                conversations.map(conv => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => loadConversation(conv.conversation_id)}
                    className="p-3 bg-purple-900/30 hover:bg-purple-900/50 rounded-lg cursor-pointer border border-purple-500/20 hover:border-purple-500/40 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">
                          {conv.title || 'Conversation sans titre'}
                        </p>
                        <p className="text-purple-300 text-xs mt-1">
                          {conv.message_count} messages · {new Date(conv.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(conv.conversation_id, e)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <Bot className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
                    <h3 className="text-purple-300 font-medium mb-2">
                      Bienvenue dans l'Assistant IA
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Je suis là pour vous aider avec SYLink Pentest.
                      <br />
                      Posez-moi vos questions!
                    </p>
                    <div className="text-xs text-purple-400 bg-purple-900/30 rounded-lg p-3 inline-block">
                      Page actuelle: <span className="font-mono">{getPageContext()}</span>
                    </div>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl p-3 ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white'
                            : msg.isError
                            ? 'bg-red-900/50 text-red-200 border border-red-500/30'
                            : 'bg-purple-900/50 text-white border border-purple-500/20'
                        }`}
                      >
                        {/* Message header */}
                        <div className="flex items-center gap-2 mb-1">
                          {msg.role === 'user' ? (
                            <User className="w-4 h-4" />
                          ) : (
                            <Bot className="w-4 h-4 text-purple-300" />
                          )}
                          <span className="text-xs opacity-70">
                            {msg.role === 'user' ? 'Vous' : 'Assistant'}
                          </span>
                        </div>

                        {/* Message content */}
                        <div className={`text-sm ${msg.role === 'user' ? 'whitespace-pre-wrap' : ''}`}>
                          {msg.role === 'assistant' ? (
                            <>
                              {msg.isStreaming && !msg.content ? (
                                <span className="inline-flex items-center gap-1 text-purple-300">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span className="text-xs">Génération en cours...</span>
                                </span>
                              ) : (
                                <>
                                  {renderMarkdown(msg.content, handleExecuteAPIAction, handleExecuteAutonomousAction)}
                                  {msg.isStreaming && <span className="inline-block w-1.5 h-4 bg-purple-400 ml-0.5 animate-pulse align-text-bottom" />}
                                </>
                              )}
                            </>
                          ) : msg.content}
                        </div>

                        {/* Action link button (for scan/report results) */}
                        {msg.actionLink && (
                          <div className="mt-3">
                            <a
                              href={msg.actionLink.url}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white text-sm rounded-lg shadow-lg hover:shadow-purple-500/25 transition-all font-medium"
                            >
                              <ExternalLink className="w-4 h-4" />
                              {msg.actionLink.label}
                            </a>
                          </div>
                        )}

                        {/* AI metadata - only show response time */}
                        {msg.role === 'assistant' && msg.ai_response_time_ms && (
                          <div className="mt-2 pt-2 border-t border-purple-500/20 text-xs text-purple-300">
                            <div className="flex items-center justify-end">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {(msg.ai_response_time_ms / 1000).toFixed(1)}s
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Feedback buttons for assistant messages */}
                      </div>
                    </div>
                  ))
                )}

                {/* Loading indicator - only shown when no streaming message exists yet */}
                {isLoading && !messages.some(m => m.isStreaming) && (
                  <div className="flex justify-start">
                    <div className="bg-purple-900/50 rounded-2xl p-3 border border-purple-500/20">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                        <span className="text-sm text-purple-300">Réflexion en cours...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Actions */}
              {suggestedActions.length > 0 && (
                <div className="px-4 py-2 border-t border-purple-500/20 bg-purple-900/30">
                  <p className="text-xs text-purple-300 mb-2 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Actions suggérées
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedActions.slice(0, 4).map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => executeAction(action)}
                        className="px-3 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 text-purple-200 text-xs rounded-full border border-purple-500/30 hover:border-purple-500/50 transition-all flex items-center gap-1"
                      >
                        {action.gpu_required && <Cpu className="w-3 h-3" />}
                        {action.name_fr || action.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Input Area */}
        {!showHistory && (
          <div className="border-t border-purple-500/20 bg-purple-950/50">
            {/* Selected Elements Display */}
            {selectedElements.length > 0 && (
              <div className="px-4 pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-purple-300 flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    Éléments sélectionnés ({selectedElements.length})
                  </span>
                  <button
                    onClick={clearSelectedElements}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors"
                  >
                    Tout effacer
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedElements.map((el, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1 px-2 py-1 bg-purple-800/50 rounded-lg text-xs text-purple-200 border border-purple-500/30"
                    >
                      <span className="text-green-400 font-mono">#{idx + 1}</span>
                      <span className="truncate max-w-[120px]">
                        {el.tagName}
                        {el.id && `#${el.id}`}
                        {el.tableData && ` (${el.tableData.totalRows} lignes)`}
                        {el.vulnerabilityContext?.cve && ` ${el.vulnerabilityContext.cve}`}
                      </span>
                      <button
                        onClick={() => removeSelectedElement(idx)}
                        className="p-0.5 hover:bg-red-500/30 rounded transition-colors"
                      >
                        <X className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setInspectorActive(true)}
                    className="flex items-center gap-1 px-2 py-1 bg-purple-600/30 hover:bg-purple-600/50 rounded-lg text-xs text-purple-300 border border-purple-500/30 border-dashed transition-colors"
                  >
                    <Crosshair className="w-3 h-3" />
                    Ajouter
                  </button>
                </div>
              </div>
            )}

            {/* Input Form */}
            <div className="p-4 pt-2">
              <form onSubmit={sendMessage} className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInspectorActive(true)}
                  className={`p-3 rounded-xl transition-colors ${
                    selectedElements.length > 0
                      ? 'bg-green-600/30 hover:bg-green-600/50 text-green-300'
                      : 'bg-purple-800/50 hover:bg-purple-700/50 text-purple-300'
                  }`}
                  title="Inspecter un élément"
                >
                  <Crosshair className="w-5 h-5" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={selectedElements.length > 0 ? "Posez une question sur les éléments sélectionnés..." : "Posez votre question..."}
                  disabled={isLoading}
                  autoFocus
                  className="flex-1 bg-purple-900/50 border border-purple-500/30 rounded-xl px-4 py-3 text-white placeholder-purple-400 focus:outline-none focus:border-purple-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="p-3 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-800 disabled:opacity-50 text-white rounded-xl transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </form>

              {/* Error message */}
              {error && (
                <div className="mt-2 p-2 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2 text-xs text-red-300">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2 bg-purple-950/80 border-t border-purple-500/10 text-center">
          <p className="text-xs text-purple-400">
            Propulsé par <span className="text-purple-300 font-medium">SYLink AI</span> · {gpuStatus?.compute_type || (gpuStatus?.has_gpu ? 'GPU' : 'CPU')}
          </p>
        </div>
      </div>

      {/* Page Inspector Component */}
      <PageInspector
        isActive={inspectorActive}
        onSelect={handleInspectorSelect}
        onClose={handleInspectorClose}
      />
    </>
  );
};

export default AIAssistantSlider;
